import React, {createContext, useContext, useEffect, useState} from "react";
import {ParamListBase} from "@react-navigation/native";
import {BottomTabBarButtonProps, createBottomTabNavigator} from "@react-navigation/bottom-tabs";
import {Feather} from "@expo/vector-icons";
import TechnicianWorkOrderDetails from "./screens/details/TechnicianWorkOrderDetails";
import TechnicianJobPlansStack from "./screens/job-plans/TechnicianJobPlansStack";
import TechnicianWorkOrderAssetsStack from "./screens/assets/TechnicianWorkOrderAssetsStack";
import TechnicianWorkLog from "./screens/work-log/TechnicianWorkLog";
import TechnicianLabour from "./screens/labour/TechnicianLabour";
import {WorkOrder, WorkOrderType} from "../../../../store/data/entities/work-order";
import {TouchableOpacity} from "react-native";
import {ScreenProps} from "../../../../utils/screen-props-helper";
import {TechnicianNavigatorParamList} from "../../TechnicianNavigator";
import {ArrowLeftOutlined} from "@ant-design/icons";
import {useLazyGetWorkOrderQuery} from "../../../../store/services/api/work-order-api";

const Tab = createBottomTabNavigator<TechnicianWorkOrdersStackParamList>();

interface WorkOrderContextProps {
  id: string | null;
  setUnseenAssetCount: (value: number) => void;
  workOrder: WorkOrder | undefined;
  workOrderFetching: boolean;
}

const WorkOrderContext = createContext<WorkOrderContextProps>(null);
export const useWorkOrder = () => useContext(WorkOrderContext);

export default function TechnicianWorkOrdersStack({navigation, route}: ScreenProps<TechnicianNavigatorParamList, 'workOrders'>) {
  const id = route?.params?.id;
  const [getWorkOrder, {data, isFetching}] = useLazyGetWorkOrderQuery();
  const [unseenAssetCount, setUnseenAssetCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (id) {
      getWorkOrder({
        id,
        includeAssets: false
      });
    }
  }, [id]);

  if (!id) {
    navigation.navigate('workOrdersList');
  }

  const DynamicTabBarButton = ({ isTabDisabled = false, ...props }: { isTabDisabled?: boolean } & BottomTabBarButtonProps) => {
    if (isTabDisabled) return null;
    return <TouchableOpacity {...props} />;
  };

  const HeaderLeft = () => (
    <TouchableOpacity
      onPress={() => navigation.navigate('workOrdersList')}
      style={{ marginHorizontal: 20 }}
    >
      <ArrowLeftOutlined style={{fontSize: 18}} />
    </TouchableOpacity>
  )

  const contextValue: WorkOrderContextProps = {
    id,
    setUnseenAssetCount,
    workOrder: data,
    workOrderFetching: isFetching
  }

  return (
    <WorkOrderContext.Provider value={contextValue}>
      <Tab.Navigator>
        <Tab.Screen
          name='details'
          component={TechnicianWorkOrderDetails}
          options={{
            title: 'Details',
            tabBarIcon: ({color, size}) => (
              <Feather name='align-left' color={color} size={size} />
            ),
            headerLeft: HeaderLeft
          }}
        />
        <Tab.Screen
          name='assets'
          component={TechnicianWorkOrderAssetsStack}
          options={{
            tabBarBadge: unseenAssetCount,
            title: 'Assets',
            headerShown: false,
            tabBarIcon: ({color, size}) => (
              <Feather name='list' color={color} size={size} />
            ),
            tabBarButton: (props) => <DynamicTabBarButton {...props} />
          }}
        />
        <Tab.Screen
          name='workLog'
          component={TechnicianWorkLog}
          options={{
            title: 'Work Log',
            tabBarIcon: ({color, size}) => (
              <Feather name='book-open' color={color} size={size} />
            ),
            tabBarButton: (props) => <DynamicTabBarButton {...props} />
          }}
        />
        <Tab.Screen
          name='labour'
          component={TechnicianLabour}
          options={{
            title: 'Labour',
            tabBarIcon: ({color, size}) => (
              <Feather name='clock' color={color} size={size} />
            ),
            tabBarButton: (props) => <DynamicTabBarButton {...props} />
          }}
        />
        <Tab.Screen
          name='jobPlans'
          component={TechnicianJobPlansStack}
          options={{
            title: 'Job Plans',
            headerShown: false,
            tabBarIcon: ({color, size}) => (
              <Feather name='book' color={color} size={size} />
            ),
            tabBarButton: (props) => (
              <DynamicTabBarButton
                isTabDisabled={data?.type !== WorkOrderType.Preventative}
                {...props}
              />
            )
          }}
        />
      </Tab.Navigator>
    </WorkOrderContext.Provider>
  );
};

export type TechnicianWorkOrdersStackParamList = ParamListBase & {
  assets: undefined;
  jobPlans: undefined;
  details: undefined;
  workLog: undefined;
  labour: undefined;
};
